import type { JobListing } from '@modelcast/types';
import { formatMoney } from '../../shared/money';

export type ApplyState = 'approved' | 'needs-approval' | 'signed-out' | 'checking';

interface JobCardProps {
  job: JobListing;
  onApply: () => void;
  applyState: ApplyState;
}

export function JobCard({ job, onApply, applyState }: JobCardProps) {
  const { room, studio, hasApplied, applicationCount } = job;
  const jobText = room.modelAdText || room.modelExpectations;
  const isEvenSplit = room.settings?.revenueSplitMode === 'even';

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatDuration = (minutes: number) => {
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${minutes}m`;
  };

  const formatCurrency = (cents: number | null) => {
    if (!cents) return null;
    return formatMoney(cents, room.currency).replace(/\.00$/, '');
  };

  const formatPricing = () => {
    if (room.pricingType === 'fixed' && room.ticketPrice) {
      return `${formatCurrency(room.ticketPrice)} ticket`;
    }
    if (room.pricingType === 'donation') {
      return room.suggestedMinimum
        ? `${formatCurrency(room.suggestedMinimum)} suggested`
        : 'Donation event';
    }
    return 'Free event';
  };

  const getApplyLabel = () => {
    if (applyState === 'checking') return 'Checking...';
    if (applyState === 'approved') return 'Apply now';
    if (applyState === 'needs-approval') return 'Apply to model first';
    return 'Sign in to apply';
  };

  const guarantee = formatCurrency(room.modelGuarantee);

  return (
    <div className="bg-white border border-line flex flex-col overflow-hidden hover:border-text hover:-translate-y-0.5 transition-all">
      {/* Card Art */}
      <div
        className="relative w-full aspect-[4/5] bg-[#d9d4c8] bg-cover bg-center"
        style={room.imageUrl ? { backgroundImage: `url(${room.imageUrl})` } : undefined}
      >
        {/* Status Pills */}
        <div className="absolute top-3.5 left-3.5 flex flex-col gap-2">
          {hasApplied && (
            <span className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-green-600 text-white rounded-full text-[10.5px] uppercase">
              Applied
            </span>
          )}
          {guarantee && (
            <span className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-white/95 rounded-full text-[10.5px] uppercase text-text">
              {guarantee} guarantee
            </span>
          )}
        </div>

        {/* Date Badge */}
        {room.scheduledAt && (
          <span className="absolute top-3.5 right-3.5 px-2.5 py-1.5 bg-white/95 rounded text-xs tabular-nums text-text">
            {formatDate(room.scheduledAt)}
          </span>
        )}

        {/* Title Overlay */}
        <span className="absolute left-4 right-4 bottom-4 text-[22px] font-normal leading-[1.12] text-white drop-shadow-[0_2px_16px_rgba(0,0,0,0.6)]">
          {room.name}
        </span>
      </div>

      {/* Card Body */}
      <div className="p-[18px] flex flex-col gap-3.5">
        {/* Studio Info */}
        {studio && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-text">{studio.name}</span>
            {studio.totalSessions > 0 && (
              <span className="text-[12.5px] text-text-dim">
                · {studio.totalSessions} session{studio.totalSessions !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}

        {/* Time & Duration */}
        <div className="flex flex-col gap-1">
          {room.scheduledAt && (
            <span className="text-sm text-text">
              {formatTime(room.scheduledAt)} · {formatDuration(room.duration)}
            </span>
          )}
        </div>

        {/* Model Expectations */}
        {jobText && (
          <p className="text-[13px] text-text-dim leading-[1.5] line-clamp-2 italic">
            {jobText}
          </p>
        )}

        {/* Studio Stats */}
        {studio && studio.totalSessions > 0 && (
          <div className="flex gap-4 pt-2 border-t border-line-soft">
            <div className="flex flex-col">
              <span className="text-[10.5px] uppercase text-text-faint">Avg turnout</span>
              <span className="text-sm font-medium text-text tabular-nums">{Math.round(studio.avgTurnout)}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-[10.5px] uppercase text-text-faint">Avg payout</span>
              <span className="text-sm font-medium text-text tabular-nums">{formatCurrency(studio.avgModelPayout) || '$0'}</span>
            </div>
          </div>
        )}

        {/* Payment Info */}
        <div className="flex flex-wrap items-center gap-3 text-[13px] text-text-dim">
          <span>{formatPricing()}</span>
          {guarantee && <span>{guarantee} guaranteed</span>}
          {isEvenSplit ? (
            <span className="italic">+ even split of tickets</span>
          ) : room.modelSplitPercent > 0 && (
            <span className="italic">+ {room.modelSplitPercent}% of tickets</span>
          )}
        </div>
      </div>

      {/* Card Footer */}
      <div className="mt-auto flex items-center gap-2 px-[18px] py-3.5 border-t border-line-soft bg-bg">
        <span className="flex-1 italic text-[12.5px] text-text-dim">
          {applicationCount} applicant{applicationCount !== 1 ? 's' : ''}
        </span>
        {hasApplied ? (
          <span className="text-[12.5px] text-green-600 font-medium">Applied</span>
        ) : (
          <button
            onClick={onApply}
            disabled={applyState === 'checking'}
            className="text-[12.5px] text-text font-medium border-b border-text pb-px hover:text-black"
          >
            {getApplyLabel()}
          </button>
        )}
      </div>
    </div>
  );
}
